<template>
  <div class="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-3 hover:shadow-xl transition-shadow duration-300">
    <!-- Header with Icon -->
    <div class="flex items-center space-x-2 mb-3">
      <div class="p-1.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg">
        <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a1 1 0 001-1V4a1 1 0 00-1-1H8a1 1 0 00-1 1v16a1 1 0 001 1z"/>
        </svg>
      </div>
      <h3 class="text-sm font-semibold text-gray-900 dark:text-white">
        {{ isWallpaperPack ? 'Devices' : 'Device' }}
      </h3>
      <div class="flex-1"></div>
      <!-- Device Count Badge -->
      <div class="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs rounded-full font-medium">
        {{ availableDevices.length }}
      </div>
    </div>
    
    <!-- Device List with Animations -->
    <div class="space-y-1">
      <button
        v-for="(device, index) in availableDevices"
        :key="device"
        @click="selectDevice(device)"
        :class="[
          'relative w-full p-2 rounded-md border transition-all duration-300 text-left flex items-center justify-between group overflow-hidden',
          selectedDevice === device
            ? 'border-blue-500 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 shadow-md scale-105'
            : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-gray-50 dark:hover:bg-gray-800 hover:scale-102'
        ]"
        :style="{ animationDelay: `${index * 100}ms` }"
        class="animate-fade-in-up"
      >
        <!-- Background Gradient Animation -->
        <div v-if="selectedDevice === device" 
             class="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 animate-pulse"></div>
        
        <!-- Device Info -->
        <div class="relative flex items-center space-x-3 flex-1">
          <!-- Device Icon -->
          <div class="flex-shrink-0">
            <div :class="[
              'p-2 rounded-lg transition-all duration-300',
              selectedDevice === device 
                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg' 
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 group-hover:text-blue-600'
            ]">
              <!-- Mobile Phone Icon -->
              <svg v-if="device.includes('mobile') || device.includes('phone') || device.includes('iphone') || device.includes('android')" 
                   class="w-4 h-4 transform group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a1 1 0 001-1V4a1 1 0 00-1-1H8a1 1 0 00-1 1v16a1 1 0 001 1z"/>
              </svg>
              <!-- Tablet Icon -->
              <svg v-else-if="device.includes('tablet')" 
                   class="w-4 h-4 transform group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
              </svg>
              <!-- Desktop Icon -->
              <svg v-else-if="device.includes('desktop') || device.includes('ultrawide')" 
                   class="w-4 h-4 transform group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
              <!-- Laptop Icon -->
              <svg v-else-if="device.includes('laptop')" 
                   class="w-4 h-4 transform group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 17h18a1 1 0 001-1V4a1 1 0 00-1-1H3a1 1 0 00-1 1v12a1 1 0 001 1zm0 0v2a1 1 0 001 1h16a1 1 0 001-1v-2"/>
              </svg>
              <!-- TV Icon -->
              <svg v-else-if="device.includes('tv')" 
                   class="w-4 h-4 transform group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2h3a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2h3z"/>
              </svg>
              <!-- Watch Icon -->
              <svg v-else-if="device.includes('watch')" 
                   class="w-4 h-4 transform group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <!-- Default Device Icon -->
              <svg v-else class="w-4 h-4 transform group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"/>
              </svg>
            </div>
          </div>
          
          <!-- Device Details -->
          <div class="flex-1 min-w-0">
            <div class="text-xs font-medium text-gray-900 dark:text-white truncate">
              {{ getDeviceDisplayName(device) }}
            </div>
            <div class="text-xs text-gray-500 dark:text-gray-400 flex items-center space-x-1">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
              <span>{{ deviceInfo[device]?.dimensions.width }}×{{ deviceInfo[device]?.dimensions.height }}</span>
            </div>
          </div>
        </div>
        
        <!-- Variation Count Badge with Animation -->
        <div class="relative flex items-center space-x-2">
          <div v-if="deviceInfo[device]?.variationCount > 1" 
               class="relative bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold shadow-lg animate-pulse hover:animate-none">
            {{ deviceInfo[device].variationCount }}
            <!-- Ping Animation -->
            <div class="absolute -inset-1 bg-green-400 rounded-full animate-ping opacity-30"></div>
          </div>
          
          <!-- Selection Indicator -->
          <div v-if="selectedDevice === device" 
               class="w-2 h-2 bg-blue-500 rounded-full animate-pulse shadow-lg">
            <div class="absolute inset-0 bg-blue-400 rounded-full animate-ping opacity-50"></div>
          </div>
        </div>
        
        <!-- Hover Effect Overlay -->
        <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out"></div>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { DeviceType, DEVICE_DISPLAY_NAMES } from '../../constants/device'

interface DeviceInfo {
  dimensions: { width: number; height: number }
  aspectRatio: string
  variationCount?: number
  hasMultipleVariations?: boolean
  totalVariations?: number
}

interface Props {
  availableDevices: DeviceType[]
  selectedDevice: DeviceType
  deviceInfo: Record<DeviceType, DeviceInfo>
  isWallpaperPack: boolean
}

defineProps<Props>()

const emit = defineEmits<{
  'device-selected': [device: DeviceType]
}>()

const getDeviceDisplayName = (deviceType: DeviceType): string => {
  return DEVICE_DISPLAY_NAMES[deviceType as DeviceType] || deviceType
}

const selectDevice = (device: DeviceType) => {
  emit('device-selected', device)
}
</script>

<style scoped>
@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fade-in-up 0.5s ease-out forwards;
}

.hover\:scale-102:hover {
  transform: scale(1.02);
}
</style>